import { useState, useEffect, useLayoutEffect, RefObject } from 'react';

interface DropdownPosition {
    top: number;
    left: number;
    width: number;
}

interface UseDropdownPositionProps {
    isOpen: boolean;
    triggerRef: RefObject<HTMLElement>;
    alignment?: 'left' | 'right';
    offsetY?: number;
    minWidth?: number;
}

export const useDropdownPosition = ({
    isOpen,
    triggerRef,
    alignment = 'left',
    offsetY = 4,
    minWidth,
}: UseDropdownPositionProps) => {
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
        top: 0,
        left: 0,
        width: 0,
    });

    // Update dropdown position
    const updateDropdownPosition = () => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const calculatedWidth = minWidth
                ? Math.max(rect.width, minWidth)
                : rect.width;

            let leftPosition = rect.left + window.scrollX;

            // Adjust for right alignment
            if (alignment === 'right') {
                leftPosition = rect.right + window.scrollX - calculatedWidth;
            }

            setDropdownPosition({
                top: rect.bottom + window.scrollY + offsetY,
                left: leftPosition,
                width: calculatedWidth,
            });
        }
    };

    // Update dropdown position when opened
    useLayoutEffect(() => {
        updateDropdownPosition();
    }, [isOpen]);

    // Update dropdown position when scrolling
    useEffect(() => {
        if (!isOpen) return;

        // Find all scrollable ancestors including the modal
        const scrollableElements: (HTMLElement | Window)[] = [];
        let element = triggerRef.current?.parentElement;

        while (element) {
            const overflowY = window.getComputedStyle(element).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollableElements.push(element);
            }
            element = element.parentElement;
        }

        // Also listen to window scroll events
        scrollableElements.push(window);

        // Add scroll event listeners to all scrollable ancestors
        const handleScroll = () => {
            updateDropdownPosition();
        };

        scrollableElements.forEach((el) => {
            if (el instanceof Window) {
                el.addEventListener('scroll', handleScroll);
            } else {
                el.addEventListener('scroll', handleScroll);
            }
        });

        // Clean up event listeners
        return () => {
            scrollableElements.forEach((el) => {
                if (el instanceof Window) {
                    el.removeEventListener('scroll', handleScroll);
                } else {
                    el.removeEventListener('scroll', handleScroll);
                }
            });
        };
    }, [isOpen]);

    return dropdownPosition;
};
